import { User } from '@heroui/react';
import { EJobRemarkStatus, type IJobRemark } from '@quesmed/types/models';
import { BiTrash } from 'react-icons/bi';
import { CiUnlock } from 'react-icons/ci';
import { FiCheckCircle } from 'react-icons/fi';
import { MdArrowOutward } from 'react-icons/md';
import { PiSealCheck } from 'react-icons/pi';
import { EFormMode } from '@/config/globals';
import { Description } from '@/core/molecules';
import { Button, Chip, ConfirmTrigger, cn, toaster } from '@/core/ui';
import { DeleteJobRemarkDocument, UpdateJobRemarkDocument } from '@/graphql/gql/graphql';
import { useMutation } from '@/libs';
import { useAuth } from '@/store/auth';
import { useRecord } from '@/store/record';
import { formatDateFromUnix } from '@/utils/date';

type RemarkProps = {
  data: IJobRemark;
  showSoftLink?: boolean;
  isWriter?: boolean;
};

export const Remark = ({ data, showSoftLink = true, isWriter = false }: RemarkProps) => {
  const isResolved = data.status === EJobRemarkStatus.RESOLVED;
  const user = useAuth.use.tokenDecoded();
  const setRecordId = useRecord.use.setRecordId();
  const setRecordModal = useRecord.use.setRecordModal();
  const [updateRemark, { loading: updateLoading }] = useMutation(UpdateJobRemarkDocument, {
    update(cache, { data }) {
      if (!data?.admin?.updateJobRemark) return;
      const { jobId, status } = data.admin.updateJobRemark;

      const increment = status === EJobRemarkStatus.RESOLVED ? 1 : -1;
      cache.modify({
        id: cache.identify({ __typename: 'Job', id: jobId }),
        fields: {
          resolvedCount(existing = 0) {
            return existing + increment;
          },
        },
      });
      // cache.modify({
      //   id: cache.identify({ __typename: 'JobRecord', recordId }),
      //   fields: {
      //     remarks(existing = [], { readField }) {
      //       return existing.map((remark: any) => {
      //         const remarkId = readField('id', remark);
      //         return remarkId === id ? data.admin.updateJobRemark : remark;
      //       });
      //     },
      //   },
      // });
    },
  });
  const [deleteRemark, { loading: deleteLoading }] = useMutation(DeleteJobRemarkDocument, {
    update(cache, { data: deleteData }, { variables }) {
      if (!deleteData?.admin?.deleteJobRemark || !variables?.id || !data.jobId) {
        return;
      }
      cache.modify({
        id: cache.identify({ __typename: 'Job', id: data.jobId }),
        fields: {
          remarkCount(existing = 0) {
            return existing - 1;
          },
        },
      });
      cache.evict({
        id: cache.identify({ __typename: 'JobRemark', id: variables.id }),
      });
      // if (data.recordId) {
      //   cache.modify({
      //     id: cache.identify({ __typename: 'JobRecord', id: data.recordId }),
      //     fields: {
      //       remarks(existing = [], { readField }) {
      //         return existing.filter((remark: any) => readField('id', remark) !== variables.id);
      //       },
      //     },
      //   });
      // }
    },
  });

  const handleResolveRemark = () => {
    if (!user?.id) return;

    toaster.toast.promise(
      async () => {
        const res = await updateRemark({
          variables: {
            id: data.id as unknown as string,
            input: {
              status: EJobRemarkStatus.RESOLVED,
              resolvedBy: user.id as unknown as string,
              resolvedAt: Date.now() / 1000,
            },
          },
        });
        if (!res.data?.admin?.updateJobRemark) {
          throw new Error('Failed to resolve the remark');
        }
      },
      {
        loading: 'Resolving the remark...',
        success: 'Successfully Resolved the remark',
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center' },
    );
  };

  const handleDeleteRemark = () => {
    toaster.toast.promise(
      async () => {
        const res = await deleteRemark({ variables: { id: data.id as unknown as string } });
        if (!res.data?.admin?.deleteJobRemark) {
          throw new Error('Failed to delete the remark');
        }
      },
      {
        loading: 'Deleting the remark...',
        success: 'Successfully Deleted the remark',
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center' },
    );
  };

  const handleReOpenRemark = () => {
    if (!user?.id) return;

    toaster.toast.promise(
      async () => {
        const res = await updateRemark({
          variables: {
            id: data.id as unknown as string,
            input: { status: EJobRemarkStatus.REOPENED },
          },
        });
        if (!res.data?.admin?.updateJobRemark) {
          throw new Error('Failed to resolve the remark');
        }
      },
      {
        loading: 'Re-opening the remark...',
        success: 'Successfully Re-Opened the remark',
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center' },
    );
  };

  const handleFixIt = () => {
    if (!data.recordId) return;
    setRecordId(data.recordId);
    setRecordModal(true, EFormMode.EDIT);
  };

  const checkIt = () => {
    if (!data.recordId) return;
    setRecordId(data.recordId);
    setRecordModal(true, EFormMode.VIEW);
  };

  return (
    <div key={data.id} className='rounded-lg bg-content1 p-3 border border-default-300 w-full'>
      <div className='flex items-start justify-between gap-3'>
        <User
          classNames={{ name: 'text-xs' }}
          name={data.createdByUser.firstName}
          description={
            <span className='text-[10px] font-normal text-default-500'>
              On <b className='text-default-600'>{formatDateFromUnix(data.createdAt as number)}</b>
              {data.recordId && (
                <>
                  &nbsp;&nbsp;&nbsp;ID: &nbsp;
                  <b title={data.recordId} className='text-default-600'>
                    {data.recordId?.slice(0, 6)}...
                  </b>
                </>
              )}
            </span>
          }
          avatarProps={{ src: '', size: 'sm' }}
        />

        <div className='flex items-center gap-x-3'>
          {!isResolved && !isWriter && (
            <ConfirmTrigger
              type='danger'
              title='Delete Remark'
              isLoading={deleteLoading}
              description={
                <Description
                  description={'Are you sure you want to delete this remark?'}
                  comment={data.remark}
                  replyCount={0}
                  type='delete'
                />
              }
              onConfirm={handleDeleteRemark}
              trigger={({ setIsOpen }) => (
                <Button
                  onPress={() => setIsOpen(true)}
                  size='sm'
                  variant='faded'
                  color='danger'
                  isIconOnly
                  className='py-0.5 px-1'
                >
                  <BiTrash className='size-3.5' />
                </Button>
              )}
            />
          )}
          {isResolved && !isWriter && (
            <ConfirmTrigger
              type='warn'
              title='Re-Open Remark'
              isLoading={deleteLoading}
              description={
                <Description
                  description={'Are you sure you want to re-open this remark?'}
                  comment={data.remark}
                  replyCount={0}
                  type='delete'
                />
              }
              onConfirm={handleReOpenRemark}
              trigger={({ setIsOpen }) => (
                <Button
                  onPress={() => setIsOpen(true)}
                  color='warning'
                  endContent={<CiUnlock className='size-3.5' />}
                  size='sm'
                >
                  Re-Open
                </Button>
              )}
            />
          )}

          {/* {mode === 'edit' && (
          <ConfirmTrigger
            type='warn'
            title='Hide Comment'
            isLoading={loading}
            description={
              <Description
                comment={reply.comment}
                replyCount={reply.replies?.length || 0}
                type='hide'
              />
            }
            onConfirm={() => handleAction(reply.id, 'hide')}
            trigger={({ setIsOpen }) => (
              <Button
                onPress={() => setIsOpen(true)}
                size='sm'
                variant='faded'
                color='warning'
                isIconOnly
                className=''
              >
                <MdOutlineHideSource className='size-4' />
              </Button>
            )}
          />
        )} */}
        </div>
      </div>

      <div
        className={cn(
          'text-xs bg-default-100 border-default-200 mt-3 wrap-break-word border rounded-lg p-2 font-mono',
          {
            // 'bg-success-50/70 border-success-200': remark.resolvedBy,
            // 'bg-warning-50/70 border-warning-200': !remark.resolvedBy,
          },
        )}
      >
        <code className={cn('text-default-800', { 'line-through decoration-default-400': isResolved })}>
          {data.remark}
        </code>
      </div>

      <div className='flex items-center gap-2 mt-4'>
        {isResolved && data.resolvedByUser ? (
          <div className='flex items-center justify-between w-full'>
            <span className='text-xs text-default-500 flex items-center gap-1'>
              <FiCheckCircle className='size-3.5' />
              Resolved by <b className='text-default-600 italic'>{data.resolvedByUser.firstName}</b> on&nbsp;
              {formatDateFromUnix(data.resolvedAt as number)}
            </span>

            {showSoftLink && (
              <Button
                onPress={checkIt}
                color='warning'
                variant='faded'
                endContent={<MdArrowOutward className='size-3.5' />}
                size='sm'
              >
                Check
              </Button>
            )}
          </div>
        ) : (
          <div className='flex items-center justify-between w-full'>
            <div className='flex items-center gap-2'>
              <ConfirmTrigger
                type='warn'
                title='Resolve Remark'
                isLoading={updateLoading}
                description={
                  <Description
                    description={'Are you sure you want to resolve this remark?'}
                    comment={data.remark}
                    replyCount={0}
                    type='resolve'
                  />
                }
                onConfirm={handleResolveRemark}
                trigger={({ setIsOpen }) => (
                  <Button
                    onPress={() => setIsOpen(true)}
                    endContent={<PiSealCheck className='size-3.5' />}
                    size='sm'
                    color='default'
                    variant='ghost'
                  >
                    Resolve
                  </Button>
                )}
              />
              {showSoftLink && data.recordId && (
                <Button
                  onPress={handleFixIt}
                  color='warning'
                  endContent={<MdArrowOutward className='size-3.5' />}
                  size='sm'
                >
                  Fix It
                </Button>
              )}
            </div>

            {data.status === EJobRemarkStatus.REOPENED && (
              <Chip variant='dot' radius='sm' color='warning' size='sm'>
                Re-Opened
              </Chip>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
